import * as React from 'react';
import { useSetAtom } from 'jotai';
import { Select, Dropdown, Button } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { usePatternQuery } from '@src/services/queries';
import ActivateFlow from '@src/features/ActivateFlow';
import FunnelChartComponent from '@src/shared/components/FunnelChart';
import SpinContainer from '@src/shared/components/SpinContainer';
import InfoTooltip from '@shared/components/InfoTooltip';
import { useAggregations, useCriticalEvents } from '@src/services/queries';
import { DEFAULT_FUNNEL_CHART_AGGREGATION } from '@src/shared/constants/analysis';
import * as store from '../../store';
import * as utils from '../../utils';
import * as Styles from './styled';
import appTracker from '@shared/utils/sensor/apptracker';

const FunnelChart = () => {
  const aggregations = useAggregations().data?.aggregations;
  const { data: criticalEvents, isLoading: isCriticalEventsLoading } = useCriticalEvents();
  const aggregation = store.useDashboardFunnelAggregation() ?? DEFAULT_FUNNEL_CHART_AGGREGATION;
  const [activateFlowOpen, setActivateFlowOpen] = React.useState(false);
  const [basePayload, { pattern }] = utils.usePatternQueryPayload();
  const onChange = store.useOnChange();
  const setActiveTab = useSetAtom(store.activeTabAtom);
  const setDropOffIndex = useSetAtom(store.dropOffIndexAtom);
  const currentAggregation = React.useMemo(
    () => aggregations?.find((it) => it.id === aggregation),
    [aggregations, aggregation],
  );
  const queryAggregations = React.useMemo(() => {
    if (!currentAggregation?.dummyId) return [currentAggregation].filter(Boolean) as Aggregation[];

    return (
      aggregations?.filter(
        (it) => it.type === 'pattern' && it.dummyId === currentAggregation.dummyId,
      ) ?? []
    );
  }, [aggregations, currentAggregation]);
  const queryAggregationIds = React.useMemo(
    () => queryAggregations.map((it) => it.id),
    [queryAggregations],
  );

  const query = usePatternQuery<number>({
    ...basePayload,
    aggregations: queryAggregationIds,
  });

  const patternEvents = React.useMemo(
    () =>
      pattern.map((step) => {
        const event = criticalEvents?.criticalEventMap.get(step);

        return {
          id: step,
          name: event?.name ?? step,
        };
      }),
    // Only re-compute when data changes to avoid chart re-rendering during loading state
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.data],
  );

  const handleFunnelMetricChange = React.useCallback(
    (value: string) => {
      onChange((draft) => {
        draft.funnel_select_aggregation = value;
      });
      // track metric change
      appTracker({
        name: 'Funnel Chart Metric Changed',
        data: {
          metric: value,
        },
      });
    },
    [onChange],
  );

  const handleDropoffClick = React.useCallback<
    GetProp<typeof FunnelChartComponent, 'onDropoffClick'>
  >(
    (index) => {
      setDropOffIndex(index);
      setActiveTab('dropoff');
    },
    [setDropOffIndex, setActiveTab],
  );

  return (
    <>
      <Styles.FunnelChartContainer>
        <Styles.FunnelChartHeader>
          <Styles.FunnelChartTitle>Funnel of Critical Events</Styles.FunnelChartTitle>
          <Styles.FunnelChartControls>
            <div>
              Metrics:
              <Select
                options={aggregations
                  ?.filter((it) => it.type === 'pattern' && !!it.dummyId)
                  .map((it) => ({
                    label: it.name,
                    value: it.id,
                    description: it.description,
                  }))}
                optionRender={(option) => (
                  <InfoTooltip title={option.data.description} placement="left">
                    {option.data.label}
                  </InfoTooltip>
                )}
                value={aggregation}
                onChange={handleFunnelMetricChange}
              />
            </div>
            <Dropdown
              trigger={['click']}
              menu={{
                items: [{ key: 'activate-flow', label: 'Activate Flow' }],
                onClick: () => setActivateFlowOpen(true),
              }}
            >
              <Button type="text" size="small" icon={<MoreOutlined />} />
            </Dropdown>
          </Styles.FunnelChartControls>
        </Styles.FunnelChartHeader>
        <SpinContainer
          autoHeight
          spinning={query.isFetching || isCriticalEventsLoading}
          empty={!query.data?.length && { type: 'chart' }}
        >
          <FunnelChartComponent
            data={query.data}
            pattern={patternEvents}
            aggregation={currentAggregation}
            tooltipAggregations={queryAggregations}
            onDropoffClick={handleDropoffClick}
          />
        </SpinContainer>
      </Styles.FunnelChartContainer>

      {activateFlowOpen && (
        <ActivateFlow
          defaultOpen
          funnelSteps={pattern}
          onClose={() => setActivateFlowOpen(false)}
        />
      )}
    </>
  );
};

export default React.memo(FunnelChart);
